import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Building2, Activity, ShieldOff, Lock, Globe, Timer, Boxes, LogOut, Loader2, Circle, TrendingUp,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../App";
import { Card, Pill, ACTION_META, Logo, StatCard } from "./ui";

function Stat(props) {
  return <StatCard {...props} />;
}

const SECTOR_TONE = { legal: "brand", accountancy: "gold", hr: "eu" };

function paddedTimeline(timeline) {
  const byDate = Object.fromEntries((timeline || []).map((r) => [r.date, r]));
  return Array.from({ length: 14 }).map((_, i) => {
    const dt = new Date();
    dt.setDate(dt.getDate() - (13 - i));
    const key = dt.toISOString().slice(0, 10);
    return byDate[key] || { date: key, total: 0, ALLOW: 0, WARN: 0, MASK: 0, BLOCK: 0 };
  });
}

export default function PlatformOverview() {
  const { platform, platformLogout } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [drill, setDrill] = useState(null);

  useEffect(() => { api.platformOverview().then(setD); }, []);

  const doLogout = () => { platformLogout(); navigate("/platform/login"); };

  return (
    <div className="lg-backdrop min-h-screen">
      <header className="sticky top-0 z-40 flex items-center justify-between gap-4 border-b border-subtle bg-surface/80 px-6 py-3.5 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <Logo className="h-9" />
          <div className="border-l border-subtle pl-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Platform Operations · internal</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden items-center gap-2 sm:flex">
            <Circle className="h-2 w-2 fill-allow text-allow" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">All systems nominal</span>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold text-white" data-testid="platform-staff-name">{platform?.staff?.name}</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{platform?.staff?.title}</div>
          </div>
          <button data-testid="platform-logout" onClick={doLogout} className="flex h-9 w-9 items-center justify-center rounded-lg border border-subtle text-slate-400 hover:border-block/40 hover:text-block" title="Sign out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1400px] px-6 py-8" data-testid="platform-overview">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Platform Overview</h1>
          <p className="mt-1 text-sm text-slate-400">
            Cross-tenant summary across every LucidGate customer. Synthetic prototype data — no billing or license management.
          </p>
        </div>

        {!d ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4 xl:grid-cols-6">
              <Stat icon={Building2} label="Tenants" value={d.platform_health.tenants} sub={`${d.platform_health.active_tenants} active`} color="#0EA5E9" />
              <Stat icon={Activity} label="Total requests" value={d.platform_health.total_requests} color="#0EA5E9" />
              <Stat icon={ShieldOff} label="Blocked" value={d.platform_health.total_blocked} color="#F43F5E" />
              <Stat icon={Lock} label="Masked" value={d.platform_health.total_masked} color="#06B6D4" />
              <Stat icon={Globe} label="EU sovereign route" value={`${d.platform_health.eu_route_pct}%`} color="#3B82F6" />
              <Stat icon={Timer} label="Avg latency" value={d.platform_health.avg_latency_ms} sub="milliseconds" color="#EAB308" />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <Boxes className="h-3.5 w-3.5" /> Sectors live:
              </span>
              {d.platform_health.sectors.map((s) => (
                <Pill key={s.sector} tone={SECTOR_TONE[s.sector] || "muted"}>{s.sector} · {s.count}</Pill>
              ))}
              <span className="ml-auto font-mono text-[10px] text-slate-400">
                {d.platform_health.simulated_generations} simulated EU generation(s)
              </span>
            </div>

            <Card className="mt-6 p-5" data-testid="platform-trends">
              <div className="mb-4 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-brand" />
                <h3 className="font-display text-sm font-bold">Requests across all tenants · last 14 days</h3>
              </div>
              {d.platform_health.total_requests === 0 ? (
                <div className="flex h-40 items-center justify-center text-sm text-slate-600">No requests recorded yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={paddedTimeline(d.timeline)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E2A3C" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                    <YAxis tick={{ fill: "#64748B", fontSize: 10 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0D131D", border: "1px solid #1E2A3C", borderRadius: 8, fontSize: 12 }} />
                    {["ALLOW", "WARN", "MASK", "BLOCK"].map((a) => (
                      <Bar key={a} dataKey={a} stackId="s" fill={ACTION_META[a].color} radius={a === "BLOCK" ? [3, 3, 0, 0] : 0} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>


            {d.tenants.some((t) => t.blocked_spike) && (
              <div data-testid="trend-alert-banner" className="mt-6 flex items-start gap-2 rounded-lg border border-warn/40 bg-warn/10 p-4">
                <span className="mt-0.5 text-warn">▲</span>
                <div className="text-sm text-slate-200">
                  <span className="font-semibold text-warn">Blocked-prompt spike detected.</span>{" "}
                  {d.tenants.filter((t) => t.blocked_spike).map((t) => t.name).join(", ")}
                  {" "}had a blocked count today above their recent daily average — worth a closer look.
                </div>
              </div>
            )}

            <Card className="mt-6 overflow-x-auto custom-scrollbar">
              <table className="w-full min-w-[820px] text-left text-sm" data-testid="platform-tenant-table">
                <thead>
                  <tr className="border-b border-subtle bg-elevated/50 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-3">Tenant</th>
                    <th className="px-4 py-3">Sector</th>
                    <th className="px-4 py-3">Users</th>
                    <th className="px-4 py-3">Requests</th>
                    <th className="px-4 py-3">Blocked</th>
                    <th className="px-4 py-3">Masked</th>
                    <th className="px-4 py-3">EU route</th>
                    <th className="px-4 py-3">Avg latency</th>
                  </tr>
                </thead>
                <tbody>
                  {d.tenants.map((t) => (
                    <tr key={t.id} data-testid="platform-tenant-row" className="border-b border-subtle/50 hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-white">
                          {t.name}
                          {t.blocked_spike && (
                            <button
                              data-testid="tenant-spike-alert"
                              onClick={() => setDrill(t)}
                              title={`Blocked spike: ${t.blocked_today} today vs ${t.blocked_avg} avg — click for daily breakdown`}
                            >
                              <Pill tone="warn">▲ blocked spike</Pill>
                            </button>
                          )}
                        </div>
                        <div className="font-mono text-[10px] text-slate-500">{t.jurisdiction}</div>
                      </td>
                      <td className="px-4 py-3"><Pill tone={SECTOR_TONE[t.sector] || "muted"}>{t.sector}</Pill></td>
                      <td className="px-4 py-3 font-mono text-slate-300">{t.users}</td>
                      <td className="px-4 py-3 font-mono text-white">{t.total}</td>
                      <td className={`px-4 py-3 font-mono ${t.blocked_spike ? "text-warn" : "text-block"}`}>
                        {t.blocked}
                        {t.blocked_spike && <span className="ml-1 text-[10px] text-warn">({t.blocked_today}↑)</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-mask">{t.masked}</td>
                      <td className="px-4 py-3 font-mono text-eu">{t.eu_route_pct}%</td>
                      <td className="px-4 py-3 font-mono text-slate-400">{t.avg_latency_ms ? `${t.avg_latency_ms} ms` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            <p className="mt-6 font-mono text-[10px] leading-relaxed text-slate-400">
              LucidGate internal view · fictional / synthetic data only. This prototype does not include billing,
              licensing or contractual data — health and volume oversight only.
            </p>
          </>
        )}
      </main>

      {drill && (() => {
        const days = drill.blocked_by_day || [];
        const maxc = Math.max(1, ...days.map((x) => x.count));
        const spikeDay = days.length ? days[days.length - 1].day : null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="spike-drilldown">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrill(null)} />
            <div className="lg-rise relative w-full max-w-lg rounded-2xl border border-subtle bg-surface p-6">
              <div className="mb-1 flex items-center justify-between">
                <h3 className="font-display text-lg font-bold text-white">Blocked prompts per day</h3>
                <button data-testid="drill-close" onClick={() => setDrill(null)} className="rounded-lg border border-subtle px-2 py-1 text-slate-400 hover:text-white">✕</button>
              </div>
              <p className="mb-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">
                {drill.name} · today {drill.blocked_today} vs avg {drill.blocked_avg}
              </p>
              <div className="space-y-1.5">
                {days.length === 0 ? (
                  <div className="text-sm text-slate-500">No blocked requests recorded.</div>
                ) : days.map((x) => (
                  <div key={x.day} data-testid="drill-day-row" className="flex items-center gap-3">
                    <span className="w-24 shrink-0 font-mono text-[11px] text-slate-400">{x.day}</span>
                    <div className="h-4 flex-1 rounded bg-elevated">
                      <div
                        className={`h-4 rounded ${x.day === spikeDay ? "bg-warn" : "bg-block/70"}`}
                        style={{ width: `${Math.round((x.count / maxc) * 100)}%` }}
                      />
                    </div>
                    <span className={`w-8 shrink-0 text-right font-mono text-xs ${x.day === spikeDay ? "text-warn" : "text-slate-300"}`}>{x.count}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 font-mono text-[10px] text-slate-500">The latest day (highlighted) exceeded this tenant's recent daily average of blocked prompts.</p>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
